/**
 * Operations Events API Contracts
 *
 * The read surface for the platform-wide operational timeline
 * (`GET /v1/operations-events`). Super-admin only — this spine is never
 * rendered to a school admin (KD2), and the gate is stated at the top of the
 * use-case rather than inherited from a router.
 *
 * The row shape itself (`OperationsEventSchema`) lives with the vocabularies in
 * `schemas/entities/operations-event.ts` and is imported, not redefined: the
 * catalog and the wire shape have to move together or a renamed event type
 * orphans history.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-08-23-0114-feat-operations-event-spine-plan.md
 */

import { z } from 'zod'
import {
  OPERATIONS_EVENT_PAGE_SIZE_MAX,
  OperationsEventClassSchema,
  OperationsEventDomainSchema,
  OperationsEventEntityTypeSchema,
  OperationsEventSchema
} from '../../schemas/entities/operations-event'

/**
 * What the reader can honestly say about instrumentation for the entity scope
 * being queried.
 *
 * `not_applicable` is for fleet/domain/filter reads that are not one entity's
 * timeline. Entity-scoped reads return either `instrumented` or
 * `not_instrumented`, so an empty page can distinguish "nothing recorded for
 * this entity" from "this entity type has no write paths yet".
 */
export const OperationsEventCoverageVerdictSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('instrumented'),
    entityType: OperationsEventEntityTypeSchema
  }),
  z.object({
    status: z.literal('not_instrumented'),
    entityType: OperationsEventEntityTypeSchema
  }),
  z.object({
    status: z.literal('not_applicable'),
    entityType: z.null()
  })
])
export type OperationsEventCoverageVerdict = z.infer<typeof OperationsEventCoverageVerdictSchema>

/**
 * GET /v1/operations-events — request contract.
 *
 * EVERY ID HERE IS A PUBLIC ID. The table's subject pointer and its three
 * rollup columns are internal `BigInt` autoincrements; accepting one from a
 * client would be an enumerable direct-object reference across every
 * operational entity in the fleet. The reader resolves public ids to internal
 * keys server-side.
 *
 * The three rollup filters are filters ONLY — they are not on the response row,
 * because they are query keys rather than display fields and serializing them
 * would cost three joins to produce public ids nothing renders.
 */
export const ListOperationsEventsRequestSchema = z
  .object({
    /** The subject's type. Must be paired with `entityId`. */
    entityType: OperationsEventEntityTypeSchema.optional(),
    /** The subject's PUBLIC id (`OperationsEvent.entityPublicId`). Must be paired with `entityType`. */
    entityId: z.string().min(1).optional(),
    domain: OperationsEventDomainSchema.optional(),
    eventClass: OperationsEventClassSchema.optional(),
    /** Inclusive lower bound on `occurredAt`, ISO-8601. */
    since: z.string().datetime().optional(),
    /** Inclusive upper bound on `occurredAt`, ISO-8601. */
    until: z.string().datetime().optional(),
    /** Rollup filter — `MobileApp.publicId`. */
    mobileAppId: z.string().min(1).optional(),
    /** Rollup filter — `PublishingAccount.publicId`. */
    publishingAccountId: z.string().min(1).optional(),
    /** Rollup filter — `AppRelease.publicId`. */
    appReleaseId: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(OPERATIONS_EVENT_PAGE_SIZE_MAX).optional(),
    /** Public id of the last event on the previous page. */
    cursor: z.string().optional()
  })
  .refine(query => (query.entityType === undefined) === (query.entityId === undefined), {
    message: 'entityType and entityId must be supplied together',
    path: ['entityId']
  })
export type ListOperationsEventsRequest = z.infer<typeof ListOperationsEventsRequestSchema>

export const ListOperationsEventsResponseSchema = z.object({
  data: z.array(OperationsEventSchema),
  coverage: OperationsEventCoverageVerdictSchema,
  /** Null when the stream is exhausted. */
  nextCursor: z.string().nullable(),
  message: z.string()
})
export type ListOperationsEventsResponse = z.infer<typeof ListOperationsEventsResponseSchema>
